import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/packages", label: "Packages & Pricing" },
  { to: "/how-we-work", label: "How We Work" },
  { to: "/about", label: "Why TWORAY" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-brandborder shadow-[0_1px_0_rgba(7,26,69,0.04)]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="header-home-link" aria-label="TWORAY home">
            <Logo variant={scrolled ? "color" : "white"} />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm font-medium link-underline pb-0.5 transition-colors ${
                    isActive ? "text-cyan" : scrolled ? "text-ink hover:text-brandblue" : "text-white/85 hover:text-cyan"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/book"
              data-testid="header-book-cta"
              className={`hidden sm:inline-flex items-center text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors duration-300 ${
                scrolled ? "bg-navy text-white hover:bg-brandblue" : "bg-cyan text-navy hover:bg-white"
              }`}
            >
              Book a Discovery Call
            </Link>
            <button
              className={`lg:hidden p-2 ${scrolled ? "text-navy" : "text-white"}`}
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-b border-brandborder" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-brandborder/60 ${isActive ? "text-brandblue" : "text-ink"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="mobile-book-cta"
              className="mt-3 text-center bg-navy text-white font-semibold px-5 py-3 rounded-sm"
            >
              Book a Discovery Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
